import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  User, Building2, Phone, Mail, MapPin, FileText, Globe, 
  Save, Plus, Trash2, Edit, Building, Briefcase, ChevronDown, ChevronUp
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SourcingManagerProfile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [myProperties, setMyProperties] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    personal: true,
    developer: true,
    mandate: true,
    properties: true
  });

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    contact_number: "",
    sourcing_type: "",
    // Developer details
    developer_name: "",
    developer_details: "",
    developer_address: "",
    developer_contact: "",
    developer_google_location: "",
    // Project details
    project_name: "",
    project_type: "",
    project_address: "",
    project_google_location: "",
    // Mandate company details
    mandate_company_name: "",
    mandate_company_details: "",
    mandate_company_address: "",
    mandate_company_contact: ""
  });

  useEffect(() => {
    fetchUserProfile();
    fetchMyProperties();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userData = response.data;
      setUser(userData);
      setFormData({
        full_name: userData.full_name || "",
        email: userData.email || "",
        contact_number: userData.contact_number || "",
        sourcing_type: userData.sourcing_type || "",
        developer_name: userData.developer_name || "",
        developer_details: userData.developer_details || "",
        developer_address: userData.developer_address || "",
        developer_contact: userData.developer_contact || "",
        developer_google_location: userData.developer_google_location || "",
        project_name: userData.project_name || "",
        project_type: userData.project_type || "",
        project_address: userData.project_address || "",
        project_google_location: userData.project_google_location || "",
        mandate_company_name: userData.mandate_company_name || "",
        mandate_company_details: userData.mandate_company_details || "",
        mandate_company_address: userData.mandate_company_address || "",
        mandate_company_contact: userData.mandate_company_contact || ""
      });
    } catch (error) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const fetchMyProperties = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to only show properties created by this user
      const userData = JSON.parse(localStorage.getItem("user") || "{}");
      const myProps = response.data.filter(p => p.created_by === userData.id || p.added_by === userData.id);
      setMyProperties(myProps);
    } catch (error) {
      console.log("Could not fetch properties");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/users/${user.id}/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local storage
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
      const updatedUser = { ...currentUser, ...formData };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm("Are you sure you want to delete this property? This action cannot be undone.")) {
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/properties/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyProperties(myProperties.filter(p => p.id !== propertyId));
      toast.success("Property deleted successfully");
    } catch (error) {
      toast.error("Failed to delete property");
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      "⚠️ WARNING: This will permanently delete your account and ALL associated data including:\n\n" +
      "• Your profile information\n" +
      "• All your properties\n" +
      "• All your leads\n" +
      "• All your documents\n" +
      "• All your notifications\n\n" +
      "This action CANNOT be undone. Are you sure you want to proceed?"
    );
    
    if (!confirmed) return;
    
    const doubleConfirm = window.confirm(
      "Are you ABSOLUTELY sure? Type 'DELETE' in the next prompt to confirm."
    );
    
    if (!doubleConfirm) return;
    
    const finalConfirm = window.prompt("Type DELETE to confirm account deletion:");
    if (finalConfirm !== "DELETE") {
      toast.error("Account deletion cancelled");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Clear local storage and redirect to login
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      toast.success("Account deleted successfully");
      navigate("/login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete account");
    }
  };

  const handleClearDeveloperDetails = () => {
    setFormData(prev => ({
      ...prev,
      developer_name: "",
      developer_details: "",
      developer_address: "",
      developer_contact: "",
      developer_google_location: ""
    }));
    toast.success("Developer details cleared. Click 'Save Changes' to confirm.");
  };

  const handleClearMandateDetails = () => {
    setFormData(prev => ({
      ...prev,
      mandate_company_name: "",
      mandate_company_details: "",
      mandate_company_address: "",
      mandate_company_contact: ""
    }));
    toast.success("Mandate company details cleared. Click 'Save Changes' to confirm.");
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0f2744] border-t-transparent" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto fade-in space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#0f2744]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              My Profile
            </h1>
            <p className="text-slate-500 mt-1">Manage your sourcing details and properties</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#c9a227] hover:bg-[#ae8220] text-white rounded-lg"
              data-testid="save-profile-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>

        {/* Personal Information */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <button
            onClick={() => toggleSection('personal')}
            className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-[#0f2744]" />
              <h2 className="font-semibold text-[#0f2744]">Personal Information</h2>
            </div>
            {expandedSections.personal ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
          </button>
          
          {expandedSections.personal && (
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="mt-1"
                  data-testid="input-full-name"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1"
                  data-testid="input-email"
                />
              </div>
              <div>
                <Label htmlFor="contact_number">Contact Number</Label>
                <Input
                  id="contact_number"
                  value={formData.contact_number}
                  onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                  className="mt-1"
                  placeholder="+91 9876543210"
                  data-testid="input-contact"
                />
              </div>
              <div>
                <Label htmlFor="sourcing_type">Sourcing Type</Label>
                <select
                  id="sourcing_type"
                  value={formData.sourcing_type}
                  onChange={(e) => setFormData({ ...formData, sourcing_type: e.target.value })}
                  className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm focus:border-[#0f2744] focus:ring-1 focus:ring-[#0f2744]"
                  data-testid="select-sourcing-type"
                >
                  <option value="">Select Type</option>
                  <option value="from_developer">From Developer</option>
                  <option value="mandate_company">Mandate Company</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Developer Details */}
        {(formData.sourcing_type === 'from_developer' || formData.sourcing_type === '') && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <button
              onClick={() => toggleSection('developer')}
              className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Building className="h-5 w-5 text-[#0f2744]" />
                <h2 className="font-semibold text-[#0f2744]">Developer & Project Details</h2>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); handleClearDeveloperDetails(); }}
                  className="text-[#0f2744] hover:text-[#1e3a5f] hover:bg-[#0f2744]/10"
                  data-testid="clear-developer-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Clear
                </Button>
                {expandedSections.developer ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
              </div>
            </button>
            
            {expandedSections.developer && (
              <div className="p-6 space-y-6">
                {/* Developer Info */}
                <div>
                  <h3 className="font-medium text-slate-700 mb-3">Developer Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="developer_name">Developer Name</Label>
                      <Input
                        id="developer_name"
                        value={formData.developer_name}
                        onChange={(e) => setFormData({ ...formData, developer_name: e.target.value })}
                        className="mt-1"
                        data-testid="input-developer-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="developer_contact">Developer Contact</Label>
                      <Input
                        id="developer_contact"
                        value={formData.developer_contact}
                        onChange={(e) => setFormData({ ...formData, developer_contact: e.target.value })}
                        className="mt-1"
                        data-testid="input-developer-contact"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="developer_address">Developer Address</Label>
                      <Input
                        id="developer_address"
                        value={formData.developer_address}
                        onChange={(e) => setFormData({ ...formData, developer_address: e.target.value })}
                        className="mt-1"
                        data-testid="input-developer-address"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="developer_google_location">Google Maps Link</Label>
                      <Input
                        id="developer_google_location"
                        value={formData.developer_google_location}
                        onChange={(e) => setFormData({ ...formData, developer_google_location: e.target.value })}
                        className="mt-1"
                        placeholder="https://maps.google.com/..."
                        data-testid="input-developer-maps"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="developer_details">Developer Details</Label>
                      <Textarea
                        id="developer_details"
                        value={formData.developer_details}
                        onChange={(e) => setFormData({ ...formData, developer_details: e.target.value })}
                        className="mt-1"
                        rows={3}
                        data-testid="input-developer-details"
                      />
                    </div>
                  </div>
                </div>

                {/* Project Info */}
                <div>
                  <h3 className="font-medium text-slate-700 mb-3">Project Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="project_name">Project Name</Label>
                      <Input
                        id="project_name"
                        value={formData.project_name}
                        onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                        className="mt-1"
                        data-testid="input-project-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="project_type">Project Type</Label>
                      <Input
                        id="project_type"
                        value={formData.project_type}
                        onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                        className="mt-1"
                        placeholder="e.g., Residential, Commercial"
                        data-testid="input-project-type"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="project_address">Project Address</Label>
                      <Input
                        id="project_address"
                        value={formData.project_address}
                        onChange={(e) => setFormData({ ...formData, project_address: e.target.value })}
                        className="mt-1"
                        data-testid="input-project-address"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="project_google_location">Project Google Maps Link</Label>
                      <Input
                        id="project_google_location"
                        value={formData.project_google_location}
                        onChange={(e) => setFormData({ ...formData, project_google_location: e.target.value })}
                        className="mt-1"
                        placeholder="https://maps.google.com/..."
                        data-testid="input-project-maps"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Mandate Company Details */}
        {(formData.sourcing_type === 'mandate_company' || formData.sourcing_type === '') && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <button
              onClick={() => toggleSection('mandate')}
              className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Briefcase className="h-5 w-5 text-[#0f2744]" />
                <h2 className="font-semibold text-[#0f2744]">Mandate Company Details</h2>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); handleClearMandateDetails(); }}
                  className="text-[#0f2744] hover:text-[#1e3a5f] hover:bg-[#0f2744]/10"
                  data-testid="clear-mandate-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Clear
                </Button>
                {expandedSections.mandate ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
              </div>
            </button>
            
            {expandedSections.mandate && (
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="mandate_company_name">Company Name</Label>
                  <Input
                    id="mandate_company_name"
                    value={formData.mandate_company_name}
                    onChange={(e) => setFormData({ ...formData, mandate_company_name: e.target.value })}
                    className="mt-1"
                    data-testid="input-mandate-name"
                  />
                </div>
                <div>
                  <Label htmlFor="mandate_company_contact">Contact Number</Label>
                  <Input
                    id="mandate_company_contact"
                    value={formData.mandate_company_contact}
                    onChange={(e) => setFormData({ ...formData, mandate_company_contact: e.target.value })}
                    className="mt-1"
                    data-testid="input-mandate-contact"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="mandate_company_address">Address</Label>
                  <Input
                    id="mandate_company_address"
                    value={formData.mandate_company_address}
                    onChange={(e) => setFormData({ ...formData, mandate_company_address: e.target.value })}
                    className="mt-1"
                    data-testid="input-mandate-address"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="mandate_company_details">Company Details</Label>
                  <Textarea
                    id="mandate_company_details"
                    value={formData.mandate_company_details}
                    onChange={(e) => setFormData({ ...formData, mandate_company_details: e.target.value })}
                    className="mt-1"
                    rows={3}
                    data-testid="input-mandate-details"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* My Properties */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <button
            onClick={() => toggleSection('properties')}
            className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-[#0f2744]" />
              <h2 className="font-semibold text-[#0f2744]">My Properties ({myProperties.length})</h2>
            </div>
            {expandedSections.properties ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
          </button>
          
          {expandedSections.properties && (
            <div className="p-6">
              {myProperties.length === 0 ? (
                <div className="text-center py-8">
                  <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-4">You haven't added any properties yet</p>
                  <Button
                    onClick={() => navigate("/properties/new")}
                    className="bg-[#0f2744] hover:bg-[#1e3a5f] text-white rounded-lg"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Property
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {myProperties.map((property) => (
                    <div
                      key={property.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-[#c9a227] transition-colors"
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        {property.media_files?.[0] ? (
                          <img
                            src={property.media_files[0]}
                            alt={property.title}
                            className="h-16 w-16 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="h-16 w-16 rounded-lg bg-slate-200 flex items-center justify-center">
                            <Building2 className="h-6 w-6 text-slate-400" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <h4 className="font-medium text-[#0f2744] truncate">{property.title}</h4>
                          <p className="text-sm text-slate-500 truncate">{property.location}</p>
                          <p className="text-sm font-semibold text-[#c9a227]">
                            ₹{property.price?.toLocaleString('en-IN')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          onClick={() => navigate(`/properties/${property.id}`)}
                          variant="ghost"
                          size="sm"
                          className="text-slate-600 hover:text-[#0f2744]"
                        >
                          View
                        </Button>
                        <Button
                          onClick={() => navigate(`/properties/${property.id}/edit`)}
                          variant="outline"
                          size="sm"
                          className="border-[#0f2744] text-[#0f2744]"
                          data-testid={`edit-property-${property.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDeleteProperty(property.id)}
                          variant="outline"
                          size="sm"
                          className="border-[#0f2744] text-[#0f2744] hover:bg-[#0f2744]/10"
                          data-testid={`delete-property-${property.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  <Button
                    onClick={() => navigate("/properties/new")}
                    variant="outline"
                    className="w-full mt-4 border-dashed border-2 border-slate-300 text-slate-600 hover:border-[#c9a227] hover:text-[#c9a227]"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add New Property
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Danger Zone - Delete Account */}
        <div className="bg-white rounded-xl border border-[#0f2744]/30 shadow-sm overflow-hidden">
          <div className="p-4 bg-[#0f2744]/5 border-b border-[#0f2744]/20">
            <h2 className="font-semibold text-[#0f2744]">Danger Zone</h2>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-slate-700">Delete Account</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Permanently delete your account and all associated data including properties, leads, and documents.
                </p>
              </div>
              <Button
                onClick={handleDeleteAccount}
                variant="outline"
                className="border-[#0f2744] text-[#0f2744] hover:bg-[#0f2744] hover:text-white"
                data-testid="delete-account-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
